// components/ui/Button.tsx - Optimized button component with consistent design
import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  // New API
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'brand' | 'gray';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  href?: string;
  
  // Legacy support (will map to new API)
  isLoading?: boolean;
  pending?: boolean;
  Icon?: React.ReactNode;
  IconAtEnd?: React.ReactNode;
  btnStyle?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'brandBtn' | 'usersettings' | 'grayBtn' | 'redBtn';
  textClassName?: string;
}

const buttonVariants = {
  primary: 'bg-brandColor hover:bg-brandColor/90 text-white shadow-md hover:shadow-lg border-2 border-brandColor',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white',
  outline: 'border-2 border-brandColor hover:bg-brandColor/5 text-brandColor',
  ghost: 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300',
  danger: 'bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-lg border-2 border-red-600',
  brand: 'bg-brandColor hover:bg-brandColor/90 text-white shadow-md hover:shadow-lg border-2 border-brandColor',
  gray: 'bg-gray-500 hover:bg-gray-600 text-white border-2 border-gray-500'
};

const buttonSizes = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-2.5 text-base',
  xl: 'px-8 py-3 text-lg'
};

const roundedVariants = {
  none: 'rounded-none',
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  full: 'rounded-full'
};

// Map legacy btnStyle to new variant
const mapLegacyStyle = (btnStyle?: string): keyof typeof buttonVariants => {
  switch (btnStyle) {
    case 'brandBtn': return 'brand';
    case 'grayBtn': return 'gray';
    case 'redBtn': return 'danger';
    case 'usersettings': return 'ghost';
    default: return (btnStyle as keyof typeof buttonVariants) || 'primary';
  }
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size = 'md',
  loading,
  leftIcon,
  rightIcon,
  fullWidth = false,
  rounded = 'md',
  href,
  disabled,
  children,
  
  // Legacy props
  isLoading,
  pending,
  Icon,
  IconAtEnd,
  btnStyle,
  textClassName,
  
  ...props
}, ref) => {
  // Handle legacy props
  const actualLoading = loading || isLoading || pending;
  const actualLeftIcon = leftIcon || Icon;
  const actualRightIcon = rightIcon || IconAtEnd;
  const actualVariant = variant || mapLegacyStyle(btnStyle);
  const isDisabled = disabled || actualLoading;

  const buttonContent = (
    <>
      {actualLoading && (
        <Loader2 className="animate-spin h-4 w-4" />
      )}
      
      {!actualLoading && actualLeftIcon && (
        <span className="inline-flex">{actualLeftIcon}</span>
      )}
      
      {children && (
        <span className={cn(textClassName)}>{children}</span>
      )}
      
      {!actualLoading && actualRightIcon && (
        <span className="inline-flex">{actualRightIcon}</span>
      )}
    </>
  );

  const buttonClasses = cn(
    // Base styles
    'inline-flex items-center justify-center gap-2 font-medium',
    'transition-all duration-200 ease-in-out',
    'focus:outline-none focus:ring-2 focus:ring-brandColor/20 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    
    // Variants
    buttonVariants[actualVariant],
    
    // Sizes  
    buttonSizes[size],
    
    // Rounded
    roundedVariants[rounded],
    
    // Full width
    fullWidth && 'w-full',
    
    // Loading state
    actualLoading && 'cursor-wait',
    
    className
  );

  // If href is provided, render as Link
  if (href && !isDisabled) {
    return (
      <Link
        to={href}
        className={buttonClasses}
      >
        {buttonContent}
      </Link>
    );
  }

  return (
    <button
      className={buttonClasses}
      disabled={isDisabled}
      ref={ref}
      {...props}
    >
      {buttonContent}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;